import React, { useCallback, useEffect, useState } from "react";
import { Dropbox } from "dropbox";
import { toastr } from "react-redux-toastr";

import Context from "./context";
import { TYPE } from "../../constants/dropbox";

interface IProps {
  dropbox: Dropbox;
}

interface MessageListenerEvent extends MessageEvent {
  data: {
    type: string;
    token?: string;
  };
}

export const DropboxProvider: React.FC<IProps> = ({ children, dropbox }) => {
  const [isSignIn, setIsSignIn] = useState(Boolean(dropbox.getAccessToken()));
  /* sign out from dropbox */
  const signOut = useCallback(() => {
    dropbox.authTokenRevoke()
      .then(() => {
        setIsSignIn(false);
      });
  }, [dropbox]);
  /* handle error. show toastr error and sign out from dropbox */
  const errorCallback = useCallback((message: string) => {
    toastr.error("Dropbox Sign In", message);
    signOut();
  }, [signOut]);
  /* handle success */
  const successCallback = useCallback((token: string) => {
    dropbox.setAccessToken(token);
    setIsSignIn(true);
  }, [dropbox]);
  /* callback for window post message from opened window */
  const messageListener = useCallback(({ origin, type, data }: MessageListenerEvent) => {
    const { location } = window;
    if (origin === location.origin && type === "message" && data.type === TYPE) {
      const { token } = data;

      if (token) {
        successCallback(token);
      } else {
        errorCallback("code");
      }
    }
  }, [successCallback, errorCallback]);
  /* add event listener to window with type "message" */
  useEffect(() => {
    window.addEventListener("message", messageListener);

    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, [messageListener]);

  return (
    <Context.Provider value={{ isSignIn, signOut }}>{children}</Context.Provider>
  );
};
